import React, { useState, useRef } from "react";
import "./createLegalDoc.css";
import { logEvent } from "firebase/analytics";
import { config } from "../config";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import GoogleButton from "../components/GoogleAuth";
import FullScreenLoader from "../components/FullScreenLoader";
import { Helmet } from "react-helmet-async";
import LeftPanel from "../components/leftPanel";
import { analytics } from "../components/firebase";
const EmployeeContract = () => {
  const googleAuthRef = useRef(null);
  const [employerDetails, setEmployerDetails] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [salaryWages, setSalaryWages] = useState("");
  const [employementType, setEmployementType] = useState("");
  const [benefits, setBenefits] = useState("");
  const [terminationTerms, setTerminationTerms] = useState("");
  const [language, setLanguage] = useState("English");
  const [errors, setErrors] = useState({});
  const [popupMessage] = useState("");
  const [isPopupVisible] = useState(false);
  const textAreaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);

  const navigate = useNavigate();
  React.useEffect(() => {
    document.documentElement.style.setProperty("--main-color", config.color);
  }, [config.color]);

  const location = useLocation();

  const initialText = location.state?.text || "";
  const receivedKey = location.state?.key || "Legal Document";
  const [Topic, setTopic] = useState(initialText);
  const [Keypoints, setKeypoints] = useState("");

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setIsButtonVisible(false);
    document.body.classList.add("no-scroll");

    try {
      const requestBody = {
        employerDetails,
        employeeDetails,
        jobDescription,
        startDate,
        salaryWages,
        employementType,
        benefits,
        terminationTerms,
        selectedLanguage: language,
      };

      console.log("About to call endpoint with payload:", requestBody);

      const generationResponse = await fetch(
        `${config.baseURL}/employment_gen.php`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      const rawResponse = await generationResponse.text();
      let data;

      try {
        data = JSON.parse(rawResponse);
      } catch {
        toast.error("Please re-try after some time");
        return;
      }

      if (data.error) {
        if (data.error === "Authentication failed") {
          
          googleAuthRef.current?.login();
          return;
        }
        if (data.redirect === "premium") {
          navigate(`/premium?id=${data.premiumId}`);
          return;
        }
        toast.error("Please re-try after some time");
        return;
      }

      if (data.success && data.resultText) {
        navigate("/result", {
          state: { result: data.resultText, history_id: data.history_id },
        });
        setEmployerDetails("");
        setEmployeeDetails("");
        setJobDescription("");
        setStartDate("");
        setSalaryWages("");
        setEmployementType("");
        setBenefits("");
        setTerminationTerms("");
        setLanguage("English");
      } else {
        toast.error("Please re-try after some time");
      }
    } catch (error) {
      toast.error("Please re-try after some time");
      logEvent(analytics, "error", {
        error_type: "Employee Contract Api Failed",
        error_message: "Failed to generate document due to API error.",
        action: "generate_button_click",
      });
    } finally {
      setIsLoading(false);
      document.body.classList.remove("no-scroll");
    }
  };

  const handleLoginSuccess = () => {
    toast.success(
      "Welcome back! Your inputs have been restored. Click Generate to continue."
    );
  };

  const handleLoginFailure = () => {};

  return (
    <>
      <Helmet key="landing">
        <title>
          Create Employment Contract Online | AI-Powered Employee Agreement
          Generator
        </title>

        <meta
          name="description"
          content="Generate legally binding employment contracts in minutes with our AI-powered document generator. Customize job title, salary, start date, and more. Trusted by HR professionals and startups."
        />

        <meta
          name="keywords"
          content="employment contract generator, employee agreement template, online employment agreement, AI legal contract, job contract form, create employee contract, HR legal form, AI employment agreement, hire letter generator, employee onboarding legal, job offer agreement, customizable employment document"
        />

        <link
          rel="canonical"
          href="https://legaldocumentgenerator.com/employment-contract-templates"
        />
      </Helmet>
      <div className="create_background">
        {isLoading && <FullScreenLoader />}
        {isPopupVisible && (
          <div className="popup-notification">
            <p>{popupMessage}</p>
          </div>
        )}

        <LeftPanel />
        <div className="create_main_templates">
            <div className="create_title">
                      <img src={config.Backbutton} alt="back" onClick={handleBackClick} />
                      Create AI Legal Document
                    </div>
          <div className="create_form_grid">
            <div className="create_inputbox1">
              Employer's name and details
              <textarea
                className={`textarea_input ${
                  errors.employerDetails ? "error" : ""
                }`}
                name="employerDetails"
                placeholder="e.g, ABC Corporation, 123 Main Street,City,State,Zip code"
                value={employerDetails}
                onChange={(e) => setEmployerDetails(e.target.value)}
                maxLength={1000}
              ></textarea>
              {errors.employerDetails && (
                <div className="error-message">{errors.employerDetails}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Employee's full name and details
              <textarea
                className={`textarea_input ${
                  errors.employeeDetails ? "error" : ""
                }`}
                name="employeeDetails"
                placeholder="e.g, John Doe, 456 Elm Street,City,State,Zip code"
                value={employeeDetails}
                onChange={(e) => setEmployeeDetails(e.target.value)}
                maxLength={1000}
              ></textarea>
              {errors.employeeDetails && (
                <div className="error-message">{errors.employeeDetails}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Job title and description
              <textarea
                className={`textarea_input ${
                  errors.jobDescription ? "error" : ""
                }`}
                name="jobDescription"
                placeholder="e.g, Marketing Manager - Responsible for overseeing the company's marketing campaigns and strategies"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                maxLength={1000}
              ></textarea>
              {errors.jobDescription && (
                <div className="error-message">{errors.jobDescription}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Start date of employment
              <input
                className={`textarea_input ${errors.startDate ? "error" : ""}`}
                name="startDate"
                placeholder="e.g, 01/01/2024"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              {errors.startDate && (
                <div className="error-message">{errors.startDate}</div>
              )}
            </div>
            <div className="create_inputbox1">
              Salary or wages
              <input
                className={`textarea_input ${
                  errors.salaryWages ? "error" : ""
                }`}
                name="salaryWages"
                placeholder="e.g, $60,000 per year or $25 per hour"
                value={salaryWages}
                onChange={(e) => setSalaryWages(e.target.value)}
              />
              {errors.salaryWages && (
                <div className="error-message">{errors.salaryWages}</div>
              )}
            </div>
            <div className="create_inputbox1">
              Employement type(full-time, part-ime, contract)
              <input
                className={`textarea_input ${
                  errors.employementType ? "error" : ""
                }`}
                name="employementType"
                placeholder="e.g., Full-time"
                value={employementType}
                onChange={(e) => setEmployementType(e.target.value)}
              />
              {errors.employementType && (
                <div className="error-message">{errors.employementType}</div>
              )}
            </div>
            <div className="create_inputbox1">
              Benefits (if applicable)
              <input
                className={`textarea_input ${errors.benefits ? "error" : ""}`}
                name="benefits "
                placeholder="e.g, Health insuarance"
                value={benefits}
                onChange={(e) => setBenefits(e.target.value)}
              />
              {errors.benefits && (
                <div className="error-message">{errors.benefits}</div>
              )}
            </div>
            <div className="create_inputbox1">
              Termination terms
              <input
                className={`textarea_input ${
                  errors.terminationTerms ? "error" : ""
                }`}
                name="terminationTerms"
                placeholder="e.g, 30 days notice by either party"
                value={terminationTerms}
                onChange={(e) => setTerminationTerms(e.target.value)}
              />
              {errors.terminationTerms && (
                <div className="error-message">{errors.terminationTerms}</div>
              )}
            </div>

            <div>
              <div className="create_inputbox1">Select Language</div>
              <select
                className="text_input"
                name="language"
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="English">English</option>
                <option value="Arabic">Arabic (RTL)</option>
                <option value="Bulgarian">Bulgarian</option>
                <option value="Chinese">Chinese (Simplified)</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Hebrew">Hebrew (RTL)</option>
                <option value="Hindi">Hindi</option>
                <option value="Indonesian">Indonesian</option>
                <option value="Italian">Italian</option>
                <option value="Japanese">Japanese</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Punjabi">Punjabi</option>
                <option value="Russian">Russian</option>
                <option value="Spanish">Spanish</option>
                <option value="Swahili">Swahili</option>
                <option value="Tagalog">Tagalog</option>
                <option value="Urdu">Urdu</option>
              </select>
            </div>
          </div>
          <ToastContainer />
          <GoogleButton
            id="loginHidden"
            ref={googleAuthRef}
            onLoginSuccess={handleLoginSuccess}
            onLoginFailure={handleLoginFailure}
          />
          <button
            className="generate_btn"
            alt="Generate"
            onClick={handleGenerate}
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );
};

export default EmployeeContract;
